import React, { ReactNode, memo, useCallback } from 'react'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import EditIcon from 'transfix-icons/build/EditIcon'
import EllipsisIcon from 'transfix-icons/build/EllipsisIcon'
import NotAvailableIcon from 'transfix-icons/build/NotAvailableIcon'
import SendIcon from 'transfix-icons/build/SendIcon'
import IconButton from 'transfix-ui/components/IconButton'
import {
  OverflowMenu,
  useOverflowToggle,
  IOverflowMenuProps,
} from 'transfix-ui/components/OverflowMenu'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { IBidApprovalStateEnum, IShipmentStates } from 'types/graphqlTypes'
import CarrierOptionFormModal from 'apps/marketplace/components/CarrierOptionFormModal'
import { brandColors } from 'transfix-ui'
import { useModalState } from 'utils/hooks'
import SendOptionForApprovalConfirmationModal from '../CarrierOptionsSidePanel/components/CarrierOptionButtons/OptionActionsButtonGroup/SendOptionForApprovalConfirmationModal'
import AcceptOptionConfirmationModal from './components/AcceptOptionConfirmationModal'
import AvailabilityConfirmationModal from './components/AvailabilityConfirmationModal'
import DeclineOptionConfirmationModal from './components/DeclineOptionConfirmationModal'
import { IOverflowMenuCarrierOption } from './types'

const useCarrierOptionOverflowMenuStyles = makeStyles(theme => ({
  menu: {
    '& .menuOption': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
  },
}))

interface IMenuOptionProps {
  title: ReactNode
  icon?: ReactNode
}

const MenuOption = memo<IMenuOptionProps>(({ icon, title }) => {
  return (
    <div className='menuOption'>
      {icon && <>{icon}</>}
      <div>{title}</div>
    </div>
  )
})

export interface ICarrierOptionOverflowMenuProps {
  option: IOverflowMenuCarrierOption
  onEditSuccess: (carrierOptionId?: string) => void
}

const CarrierOptionOverflowMenu = memo<ICarrierOptionOverflowMenuProps>(
  ({ onEditSuccess, option }) => {
    const classes = useCarrierOptionOverflowMenuStyles()

    const { id: optionId, latestBidApproval, shipment, shipmentId, status } = option || {}
    const { state: shipmentState } = shipment || {}
    const { state: bidApprovalState } = latestBidApproval || {}
    const isOptionAvailable = status === null
    const isOptionAccepted = status === 'Accepted'

    const { clickOverflowTrigger, expanded, setExpanded } = useOverflowToggle()
    const { createToggleHandler, modalState } = useModalState({
      acceptModal: false,
      availabilityModal: false,
      declineModal: false,
      editModal: false,
      sendForApprovalModal: false,
    })
    const {
      acceptModal: isAcceptModalOpen,
      availabilityModal: isAvailabilityModalOpen,
      declineModal: isDeclineModalOpen,
      editModal: isEditModalOpen,
      sendForApprovalModal: isSendForApprovalModalOpen,
    } = modalState || {}
    const openAcceptModal = createToggleHandler('acceptModal', true)
    const closeAcceptModal = createToggleHandler('acceptModal', false)
    const openAvailabilityModalHandler = createToggleHandler('availabilityModal', true)
    const closeAvailabilityModalHandler = createToggleHandler('availabilityModal', false)
    const openSendForApprovalModalHandler = createToggleHandler('sendForApprovalModal', true)
    const closeSendForApprovalModalHandler = createToggleHandler('sendForApprovalModal', false)
    const openDeclineModalHandler = createToggleHandler('declineModal', true)
    const closeDeclineModalHandler = createToggleHandler('declineModal', false)
    const openEditModalHandler = createToggleHandler('editModal', true)
    const closeEditModalHandler = createToggleHandler('editModal', false)

    const onSuccess = useCallback(
      (carrierOptionId?: string) => {
        if (isAcceptModalOpen) {
          closeAcceptModal()
        } else if (isAvailabilityModalOpen) {
          closeAvailabilityModalHandler()
        } else if (isDeclineModalOpen) {
          closeDeclineModalHandler()
        } else if (isEditModalOpen) {
          closeEditModalHandler()
        } else if (isSendForApprovalModalOpen) {
          closeSendForApprovalModalHandler()
        }
        onEditSuccess(carrierOptionId)
      },
      [
        closeAcceptModal,
        closeAvailabilityModalHandler,
        closeDeclineModalHandler,
        closeEditModalHandler,
        closeSendForApprovalModalHandler,
        isAcceptModalOpen,
        isAvailabilityModalOpen,
        isDeclineModalOpen,
        isEditModalOpen,
        isSendForApprovalModalOpen,
        onEditSuccess,
      ]
    )

    const hasDisabledAcceptMenuOption = shipmentState !== IShipmentStates.tendered

    const hasGetApprovalMenuOption =
      !bidApprovalState || bidApprovalState === IBidApprovalStateEnum.declined

    const hasPendingApprovalMenuOption = bidApprovalState === IBidApprovalStateEnum.open

    const hasAcceptApprovedMenuOption = bidApprovalState === IBidApprovalStateEnum.approved

    const getAcceptMenuOption: IOverflowMenuProps['options'] = [
      {
        itemComponent: <MenuOption title='Accept' icon={<CheckOutlineIcon size='large' />} />,
        label: 'accept',
        action: openAcceptModal,
        disabled: hasDisabledAcceptMenuOption,
      },
    ]

    const getAcceptApprovedMenuOption: IOverflowMenuProps['options'] = [
      {
        itemComponent: (
          <MenuOption
            title={
              <>
                Accept <span style={{ color: brandColors.green3 }}>(Approved)</span>
              </>
            }
            icon={<CheckOutlineIcon size='large' />}
          />
        ),
        label: 'accept-approved',
        action: openAcceptModal,
        disabled: hasDisabledAcceptMenuOption,
      },
    ]

    const getApprovalMenuOption: IOverflowMenuProps['options'] = [
      {
        itemComponent: <MenuOption title='Get Approval' icon={<SendIcon size='large' />} />,
        label: 'get-approval',
        action: openSendForApprovalModalHandler,
      },
    ]

    const pendingApprovalMenuOption: IOverflowMenuProps['options'] = [
      {
        itemComponent: <MenuOption title='Pending Approval' icon={<SendIcon size='large' />} />,
        label: 'pending-approval',
        action: () => {},
        disabled: true,
      },
    ]

    const availableOptionMenu: IOverflowMenuProps['options'] = [
      ...(hasAcceptApprovedMenuOption ? getAcceptApprovedMenuOption : getAcceptMenuOption),
      ...(hasGetApprovalMenuOption ? getApprovalMenuOption : []),
      ...(hasPendingApprovalMenuOption ? pendingApprovalMenuOption : []),
      {
        itemComponent: <MenuOption title='Decline' icon={<NotAvailableIcon size='large' />} />,
        label: 'decline',
        action: openDeclineModalHandler,
      },
      {
        itemComponent: <MenuOption title='Edit' icon={<EditIcon size='large' />} />,
        label: 'edit',
        action: openEditModalHandler,
      },
    ]

    const markAsAvailableOption: IOverflowMenuProps['options'] = [
      {
        itemComponent: <MenuOption title='Mark as Available' />,
        label: 'mark-as-available',
        action: openAvailabilityModalHandler,
      },
    ]

    const unavailableOptionMenu: IOverflowMenuProps['options'] = [
      ...(isOptionAccepted ? [] : markAsAvailableOption),
    ]

    const options = isOptionAvailable ? availableOptionMenu : unavailableOptionMenu

    return (
      <>
        <OverflowMenu
          options={options}
          onExpanded={setExpanded}
          expanded={expanded}
          dropdownId={optionId}
          dataTest='option-overflow-menu'
          className={classes.menu}
          trigger={
            <IconButton
              Icon={EllipsisIcon}
              label='menu'
              onClick={clickOverflowTrigger}
              color='tertiary'
              size='large'
            />
          }
        />
        {isAcceptModalOpen && (
          <AcceptOptionConfirmationModal
            option={option}
            onClose={closeAcceptModal}
            onSuccess={onSuccess}
          />
        )}
        {isAvailabilityModalOpen && (
          <AvailabilityConfirmationModal
            optionId={optionId}
            isAvailable={isOptionAvailable}
            onClose={closeAvailabilityModalHandler}
            onSuccess={onSuccess}
          />
        )}
        {isDeclineModalOpen && (
          <DeclineOptionConfirmationModal
            shipmentId={shipmentId}
            optionId={optionId}
            onClose={closeDeclineModalHandler}
            onSuccess={onSuccess}
          />
        )}
        {isEditModalOpen && (
          <CarrierOptionFormModal
            shipmentId={shipmentId}
            carrierOption={option}
            onClose={closeEditModalHandler}
            onSuccess={onSuccess}
          />
        )}
        {isSendForApprovalModalOpen && (
          <SendOptionForApprovalConfirmationModal
            optionId={optionId}
            onSuccess={onSuccess}
            onClose={closeSendForApprovalModalHandler}
          />
        )}
      </>
    )
  }
)

export default CarrierOptionOverflowMenu
